import { BrowserRouter, Routes, Route, useLocation, Navigate } from "react-router-dom";
import { useState, useEffect } from "react";
import { Toaster } from "sonner";
import Navbar from "./components/Navbar";
import Landing from "./pages/Landing";
import Login from "./pages/Login";
import Register from "./pages/Register";
import ForgotPassword from "./pages/ForgotPassword";
import ResetPassword from "./pages/ResetPassword";
import Onboarding from "./pages/Onboarding";
import KVKK from "./pages/KVKK";
import AuthCallback from "./pages/AuthCallback";
import Dashboard from "./pages/Dashboard";
import Profile from "./pages/Profile";
import Settings from "./pages/Settings";
import Messages from "./pages/Messages";
import Notifications from "./pages/Notifications";
import Games from "./pages/Games";
import CreateLeague from "./pages/CreateLeague";
import LeagueWizard from "./pages/LeagueWizard";
import LeagueDetail from "./pages/LeagueDetail";
import LeagueStats from "./pages/LeagueStats";
import WeekEntry from "./pages/WeekEntry";
import WeekDetail from "./pages/WeekDetail";
import Statistics from "./pages/Statistics";
import Discover from "./pages/Discover";
import Tournaments from "./pages/Tournaments";
import CreateTournament from "./pages/CreateTournament";
import TournamentDetail from "./pages/TournamentDetail";
import CreateCustomGame from "./pages/CreateCustomGame";
import Badges from "./pages/Badges";
import SearchPage from "./pages/SearchPage";
// Online Game
import { OkeyGameRoom } from "./components/okey";
// Admin Pages
import AdminDashboard from "./pages/admin/AdminDashboard";
import AdminUsers from "./pages/admin/AdminUsers";
import AdminLeagues from "./pages/admin/AdminLeagues";
import AdminGames from "./pages/admin/AdminGames";
import AdminSettings from "./pages/admin/AdminSettings";
import AdminLogs from "./pages/admin/AdminLogs";
import AdminAnnouncements from "./pages/admin/AdminAnnouncements";
import AdminSystemStatus from "./pages/admin/AdminSystemStatus";
import { AuthProvider, useAuth } from "./contexts/AuthContext";
import "./App.css";

// REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH

function ProtectedRoute({ children }) {
  const { user, loading, checkAuth } = useAuth();
  const location = useLocation();

  useEffect(() => {
    // Skip if user data was passed from AuthCallback
    if (location.state?.user) return;
    checkAuth();
  }, []);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="w-8 h-8 border-2 border-[#4DFFFF] border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  return children;
}

function AppRouter() {
  const location = useLocation();
  
  // Check URL fragment for session_id (Google Auth callback)
  // This must be synchronous - NOT in useEffect
  if (location.hash?.includes('session_id=')) {
    return <AuthCallback />;
  }

  return (
    <Routes>
      {/* Public Routes */}
      <Route path="/" element={<Landing />} />
      <Route path="/login" element={<Login />} />
      <Route path="/register" element={<Register />} />
      <Route path="/forgot-password" element={<ForgotPassword />} />
      <Route path="/reset-password/:token" element={<ResetPassword />} />
      <Route path="/kvkk" element={<KVKK />} />
      
      {/* Onboarding - Protected but no navbar */}
      <Route path="/onboarding" element={
        <ProtectedRoute>
          <Onboarding />
        </ProtectedRoute>
      } />
      
      {/* Protected Routes */}
      <Route path="/dashboard" element={
        <ProtectedRoute>
          <Navbar />
          <Dashboard />
        </ProtectedRoute>
      } />
      <Route path="/profile/:username" element={
        <>
          <Navbar />
          <Profile />
        </>
      } />
      <Route path="/settings" element={
        <ProtectedRoute>
          <Navbar />
          <Settings />
        </ProtectedRoute>
      } />
      <Route path="/messages" element={
        <ProtectedRoute>
          <Navbar />
          <Messages />
        </ProtectedRoute>
      } />
      <Route path="/messages/:userId" element={
        <ProtectedRoute>
          <Navbar />
          <Messages />
        </ProtectedRoute>
      } />
      <Route path="/notifications" element={
        <ProtectedRoute>
          <Navbar />
          <Notifications />
        </ProtectedRoute>
      } />
      <Route path="/games" element={
        <>
          <Navbar />
          <Games />
        </>
      } />
      <Route path="/create-league" element={
        <ProtectedRoute>
          <Navbar />
          <LeagueWizard />
        </ProtectedRoute>
      } />
      <Route path="/create-league-simple" element={
        <ProtectedRoute>
          <Navbar />
          <CreateLeague />
        </ProtectedRoute>
      } />
      <Route path="/league/:leagueId" element={
        <>
          <Navbar />
          <LeagueDetail />
        </>
      } />
      <Route path="/league/:leagueId/stats" element={
        <>
          <Navbar />
          <LeagueStats />
        </>
      } />
      <Route path="/league/:leagueId/week/new" element={
        <ProtectedRoute>
          <Navbar />
          <WeekEntry />
        </ProtectedRoute>
      } />
      <Route path="/league/:leagueId/week/:weekId" element={
        <>
          <Navbar />
          <WeekDetail />
        </>
      } />
      <Route path="/league/:leagueId/week/:weekId/edit" element={
        <ProtectedRoute>
          <Navbar />
          <WeekEntry />
        </ProtectedRoute>
      } />
      <Route path="/game/:roomId" element={
        <ProtectedRoute>
          <OkeyGameRoom />
        </ProtectedRoute>
      } />
      <Route path="/statistics" element={
        <>
          <Navbar />
          <Statistics />
        </>
      } />
      <Route path="/discover" element={
        <>
          <Navbar />
          <Discover />
        </>
      } />
      <Route path="/search" element={
        <>
          <Navbar />
          <SearchPage />
        </>
      } />
      <Route path="/tournaments" element={
        <ProtectedRoute>
          <Navbar />
          <Tournaments />
        </ProtectedRoute>
      } />
      <Route path="/create-tournament" element={
        <ProtectedRoute>
          <Navbar />
          <CreateTournament />
        </ProtectedRoute>
      } />
      <Route path="/tournament/:tournamentId" element={
        <>
          <Navbar />
          <TournamentDetail />
        </>
      } />
      <Route path="/create-custom-game" element={
        <ProtectedRoute>
          <Navbar />
          <CreateCustomGame />
        </ProtectedRoute>
      } />
      <Route path="/badges" element={
        <>
          <Navbar />
          <Badges />
        </>
      } />
      
      {/* Admin Routes */}
      <Route path="/admin" element={
        <ProtectedRoute>
          <AdminDashboard />
        </ProtectedRoute>
      } />
      <Route path="/admin/users" element={
        <ProtectedRoute>
          <AdminUsers />
        </ProtectedRoute>
      } />
      <Route path="/admin/leagues" element={
        <ProtectedRoute>
          <AdminLeagues />
        </ProtectedRoute>
      } />
      <Route path="/admin/games" element={
        <ProtectedRoute>
          <AdminGames />
        </ProtectedRoute>
      } />
      <Route path="/admin/settings" element={
        <ProtectedRoute>
          <AdminSettings />
        </ProtectedRoute>
      } />
      <Route path="/admin/logs" element={
        <ProtectedRoute>
          <AdminLogs />
        </ProtectedRoute>
      } />
      <Route path="/admin/announcements" element={
        <ProtectedRoute>
          <AdminAnnouncements />
        </ProtectedRoute>
      } />
      <Route path="/admin/system-status" element={
        <ProtectedRoute>
          <AdminSystemStatus />
        </ProtectedRoute>
      } />
    </Routes>
  );
}

function App() {
  return (
    <div className="min-h-screen">
      <BrowserRouter>
        <AuthProvider>
          <AppRouter />
        </AuthProvider>
      </BrowserRouter>
      <Toaster 
        position="top-right" 
        toastOptions={{
          style: {
            background: 'rgba(10, 22, 53, 0.95)',
            border: '1px solid rgba(77, 255, 255, 0.3)',
            color: 'white',
          },
        }}
      />
    </div>
  );
}

export default App;
